/** Startseite: Was ist heute zu tun? */
import { useMemo, useState } from 'react';
import { Link } from 'react-router';
import { fmtDateLong, openToday, streakInfo, allTaskTypes, zonedParts, type TaskStatus } from '@pflanzen/shared';
import { useStore } from '../lib/store';
import { useNow, useStatuses } from '../lib/hooks';
import { useWeather } from '../lib/weather';
import { CheckSheet } from '../ui/CheckSheet';
import { TaskRow } from '../ui/TaskRow';
import { AdviceCard, WeatherMini } from '../ui/WeatherBits';
import { SectionTitle } from '../ui/kit';

function greeting(now: Date, tz: string): string {
  const h = zonedParts(now, tz).hour;
  if (h < 5) return 'Gute Nacht';
  if (h < 11) return 'Guten Morgen';
  if (h < 17) return 'Hallo';
  return 'Guten Abend';
}

export function TodayPage() {
  const { state } = useStore();
  const now = useNow();
  const { data: wx } = useWeather();
  const climate = useMemo(() => (wx ? { indoorRh: wx.climate.indoorRh } : undefined), [wx]);
  const statuses = useStatuses(state, now, climate);
  const [checking, setChecking] = useState<TaskStatus | null>(null);
  const [levelOpen, setLevelOpen] = useState(false);
  if (!state) return null;
  const tz = state.settings.timezone;

  const open = openToday(statuses);
  const handled = statuses.filter((s) => s.handledToday);
  const soon = statuses.filter((s) => s.state === 'soon' || s.state === 'snoozed').slice(0, 6);
  const streak = streakInfo(state.events, allTaskTypes(state.taskTypes), state.settings, now);
  const urgent = (wx?.advice ?? []).filter((a) => a.level === 'alert' || a.level === 'warn');
  const name = state.settings.userName;

  return (
    <div className="px-4 pb-6">
      <header className="pt-safe rise pb-2">
        <p className="text-sm text-muted">{fmtDateLong(now, tz)}</p>
        <h1 className="font-display mt-0.5 text-[34px] leading-tight">
          {greeting(now, tz)}{name ? `, ${name}` : ''}
        </h1>
        <div className="mt-2 flex items-center gap-2 text-sm">
          <button type="button" onClick={() => setLevelOpen(true)} className="min-h-8 rounded-full bg-moss-soft px-2.5 py-1 font-semibold text-moss active:scale-95">
            {streak.level.icon} {streak.level.name} · {streak.points}
          </button>
          {streak.current > 1 && <span className="text-muted">🔥 {streak.current} Tage pünktlich geprüft</span>}
        </div>
      </header>

      {urgent.length > 0 && (
        <div className="mt-3 space-y-2.5">
          {urgent.slice(0, 2).map((a) => (
            <AdviceCard key={a.id} advice={a} />
          ))}
        </div>
      )}
      <div className="mt-3">
        <WeatherMini view={wx} />
      </div>

      <SectionTitle right={open.length > 0 ? <span className="text-sm font-semibold text-moss">{open.length} offen</span> : null}>
        Heute
      </SectionTitle>
      {open.length === 0 ? (
        <div className="rise rounded-3xl border border-line bg-card p-5 text-center">
          <div className="text-4xl">🌿</div>
          <p className="font-display mt-2 text-xl">Alles erledigt</p>
          <p className="mt-1 text-sm text-muted">Deine Pflanzen sind versorgt. Schau morgen wieder vorbei.</p>
        </div>
      ) : (
        <div className="space-y-2.5">
          {open.map((s) => (
            <TaskRow key={s.task.id} status={s} onCheck={setChecking} />
          ))}
        </div>
      )}

      {handled.length > 0 && (
        <>
          <SectionTitle>Heute erledigt</SectionTitle>
          <div className="space-y-2">
            {handled.map((s) => (
              <TaskRow key={s.task.id} status={s} onCheck={setChecking} compact />
            ))}
          </div>
        </>
      )}

      {soon.length > 0 && (
        <>
          <SectionTitle right={<Link to="/pflanzen" className="text-sm font-semibold text-moss">Alle ›</Link>}>Demnächst</SectionTitle>
          <div className="space-y-2">
            {soon.map((s) => (
              <TaskRow key={s.task.id} status={s} onCheck={setChecking} compact />
            ))}
          </div>
        </>
      )}

      {streak.savedWaterings > 0 && (
        <p className="mt-6 text-center text-xs text-muted">
          💧 {streak.savedWaterings}× nicht gegossen, weil die Erde noch feucht war – so geht Übergießen nicht.
        </p>
      )}

      <CheckSheet status={checking} onClose={() => setChecking(null)} />
    </div>
  );
}
